// src/components/revenue/RevenueBridgePanel.js
//
// Two jobs, and the first is the important one.
//
// 1. SYNC HEALTH. Nothing feeds the order mirror except an hourly job. If it
//    stops, every windowed read returns an empty connection and the dashboard
//    renders a confident $0 — no error, HTTP 200, a chart of zero. That has
//    happened before and was caught only by manual inspection.
//
//    The engine now records a heartbeat and the API reports it, but until this
//    component nothing displayed it. A protection nobody can see is not a
//    protection, so the banner is deliberately loud and sits ABOVE the numbers it
//    invalidates rather than on a health page nobody opens.
//
//    Three states, kept distinct on purpose:
//      stale === true   the sync has stopped     -> DANGER, numbers are suspect
//      stale === false  healthy                  -> quiet confirmation
//      stale === null   UNKNOWN, not healthy     -> warning, not silence
//    Collapsing null into "fine" would be the exact failure mode being guarded
//    against.
//
// 2. THE RECONCILIATION. When someone asks why revenue is lower than expected,
//    these lines are the answer: cancelled, test orders, refunds, and the
//    charge variance. Without them a correct reduction looks like lost business.
import React from "react";

const money = (n, ccy = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(n || 0));

// A deduction reads as a reduction, so it is shown signed and coloured. Printing
// "127.82" next to "1,345.69" invites the reader to add rather than subtract.
function Row({ label, value, currency, deduction = false, muted = false, strong = false, hint }) {
  const cls = [
    "d-flex justify-content-between align-items-baseline py-1",
    muted ? "text-inverse text-opacity-50" : "",
  ].join(" ");
  return (
    <div className={cls}>
      <div className={strong ? "fw-bold" : ""}>
        {label}
        {hint ? (
          <div className="small text-inverse text-opacity-50">{hint}</div>
        ) : null}
      </div>
      <div className={(strong ? "fw-bold " : "") + (deduction ? "text-danger" : "")}>
        {deduction && Number(value || 0) !== 0 ? "− " : ""}
        {money(Math.abs(Number(value || 0)), currency)}
      </div>
    </div>
  );
}

function StaleBanner({ mirror }) {
  if (!mirror) return null;

  if (mirror.available === false) {
    return (
      <div className="alert alert-warning mb-3">
        <strong>Sync health unknown.</strong> The mirror could not be reached, so these
        figures cannot be confirmed current.
      </div>
    );
  }

  if (mirror.stale === true) {
    return (
      <div className="alert alert-danger mb-3">
        <strong>These numbers are stale — do not rely on them.</strong>
        <div className="small mt-1">
          {mirror.staleReason || "The order sync has stopped."} Orders placed since then are
          missing, so revenue is understated rather than simply low.
        </div>
      </div>
    );
  }

  if (mirror.stale === null || typeof mirror.stale === "undefined") {
    // Deliberately a warning, not silence. "We don't know" must never render the
    // same as "we're fine" — that equivalence is what let a dead sync look healthy.
    return (
      <div className="alert alert-warning mb-3">
        <strong>Sync health unknown.</strong>
        <div className="small mt-1">
          {mirror.staleReason || "No sync heartbeat has been recorded yet."} The figures below
          may be current — but nothing is confirming it.
        </div>
      </div>
    );
  }

  return (
    <div className="small text-inverse text-opacity-50 mb-3">
      Sync healthy — last successful run{" "}
      {mirror.syncAgeMinutes != null ? `${mirror.syncAgeMinutes} min ago` : "recently"}
      {mirror.orders != null ? ` · ${mirror.orders.toLocaleString()} orders mirrored` : ""}
    </div>
  );
}

export default function RevenueBridgePanel({ data, loading, error }) {
  if (loading) return <div className="text-inverse text-opacity-50">Loading revenue bridge…</div>;
  if (error) return <div className="alert alert-danger">Could not load the revenue bridge: {error}</div>;
  if (!data) return null;

  // Not an error: the bridge is backed by the commerce mirror, which holds USD
  // only. Saying so is more useful than an empty panel.
  if (data.supported === false) {
    return (
      <div className="alert alert-info">
        <strong>Not available for this currency.</strong>
        <div className="small mt-1">{data.reason}</div>
      </div>
    );
  }

  const b = data.bridge || {};
  const ccy = data.currency || "USD";

  return (
    <div>
      <StaleBanner mirror={data.mirror} />

      <Row label="Gross" value={b.gross} currency={ccy} strong
           hint="Everything in the window, including cancellations and test orders" />
      <Row label="Cancelled" value={b.cancelled} currency={ccy} deduction
           hint="Never charged" />
      <Row label="Test orders" value={b.testOrders} currency={ccy} deduction
           hint="QA / rehearsal orders — tagged, not real business" />

      {/* Published even when zero. It is the residual between charged amounts and
          order totals, and a NON-zero value is a real signal about charge drift —
          hiding it when small would mean nobody notices when it grows. */}
      <Row
        label="Charge variance"
        value={b.chargeVariance}
        currency={ccy}
        muted={!Number(b.chargeVariance)}
        hint="Difference between what was actually charged and what the order totals imply"
      />

      <hr className="my-2" />
      <Row label="Captured" value={b.captured} currency={ccy} strong
           hint="What was actually collected" />
      <Row label="Refunds" value={b.refunds} currency={ccy} deduction />
      {Number(b.pendingRefunds) ? (
        <Row label="Pending refunds" value={b.pendingRefunds} currency={ccy} muted
             hint="Granted but not yet settled — deliberately NOT in net" />
      ) : null}

      <hr className="my-2" />
      <Row label="Net" value={b.net} currency={ccy} strong />

      {data.refundsAvailable === false ? (
        <div className="alert alert-warning mt-3 mb-0 small">
          Refund data is not loaded for this database, so the refund line reads zero because
          it is <em>unknown</em>, not because no refunds happened.
        </div>
      ) : null}
    </div>
  );
}
